import React from 'react';
import ContentLayout from '../../../../components/ContentLayout';
import Collapisble from '../../../../components/Collapsible';
import props from '../../../../lib/contentLayoutProps/learning-at-kingston/early-years/layoutProps';

/* eslint-disable react/prop-types */
export default () => (
  <ContentLayout {...props} locale="en" pageKey="ibpyp-at-kik">
    <h1>Primary Years Programme (IBPYP) at Kingston International Kindergarten</h1>
    <p>
      Kingston International School is the first school in Hong Kong authorized to offer the International Baccalaureate
      Organization - Primary Years Programme. This programme was specially developed for children ages three to twelve
      years. It is an inquiry-based approach to learning that is truly international in its outlook.
    </p>
    <p>
      The Primary Years Programme concentrates on developing the whole child and values a healthy body as well as a
      healthy mind. The programme encourages students to be thinkers and problem solvers while nourishing the creative
      and caring aspects of their personalities. It is recognized internationally for its excellence and prepares
      students well for further studies either here in Hong Kong or overseas. Kingston International School incorporates
      the IBO philosophy into all aspects of school life.
    </p>

    <h3>The Transdisciplinary Nature of the Programme</h3>
    <p>
      At Kingston we acknowledge the importance of particular subject areas: language; mathematics; social studies;
      science; arts; personal, social and physical education.
    </p>
    <p>
      The knowledge, concepts and skills that constitute each of these subject areas are documented in detailed
      frameworks—scope and sequences—that set out the overall expectations for each subject.
    </p>
    <p>
      We also recognize that educating students in a set of isolated subject areas while necessary, is not sufficient.
      Of equal importance is the need to acquire skills in context, and to explore content that is relevant to students
      and transcends the boundaries of the traditional subjects. “To be truly educated, a student must also make
      connections across the disciplines, discover ways to integrate the separate subjects, and ultimately relate what
      they learn to life” (Boyer 1995: 82).
    </p>
    <p>
      The programme defines transdisciplinary themes that identify areas of shared human experience and have meaning for
      individuals from different cultures and ethnicities. These themes are part of the common ground that unifies the
      learning in all IB World Schools offering the PYP. They provide the opportunity to incorporate both local and
      global issues in the knowledge component of the PYP written curriculum—what we want students to know about. There
      are six transdisciplinary themes in the PYP and kindergartens are required to cover four of these. The six
      transdisciplinary themes are Who We Are, How We Express Ourselves, Where We Are in Place and Time, Sharing the
      Planet, How We Organise Ourselves and How the World Works. Through these transdisciplinary themes our students
      inquire into and learn about both locally and globally important issues. These units have been developed
      collaboratively by our team of English and Chinese staff to ensure that they meet the needs of young learners and
      are a means by which key attitudes, skills, concepts, knowledge and the Learner Profile are developed.
    </p>

    <Collapisble trigger="Bilingual Studies">
      <React.Fragment>
        <p>
          The goal of language learning at Kingston is to develop a child's competence and confidence when
          communicating. This is why all our classes have one native speaking fully qualified English teacher and one
          native speaking fully qualified Putonghua teacher. The learning environment supports bilingual language
          learning by allowing the children extended and unhurried opportunities to observe, listen, respond, ask
          questions and participate in conversations that help make new experiences meaningful. This occurs
          simultaneously in both languages, as children are free to use both English and Putonghua at all times
          throughout their day. Circle Times each day provide more specific time to focus on each language separately.
        </p>
        <p>
          Our aim for the 3 and 4 Year Old Classes is to develop in children the desire to read and write. For children
          to become successful readers and writers they need to have a love of reading, of books, and of language. For
          children to learn to write they have to WANT to write. Children learn about reading and writing when they are
          speaking and listening. They learn about reading when they explore writing, and their oral language improves
          as they read and write. Every experience that the teachers and children are involved in within the classroom
          help to develop children's reading and writing. Children in KIK become more competent and confident with
          expressing themselves in both languages. Children are encouraged to support their peers in their language
          learning by using both English and Putonghua in play situations. Rather than correcting a child's language,
          teachers model the correct language and sentence structure, praising the child's efforts and attempts as they
          do so. They also challenge and extend children's verbal ability with an extensive range of more complex
          vocabulary. This especially occurs related to PYP Units of Inquiry.
        </p>
        <h5>Writing</h5>
        <p>
          Teachers foster the development of pre-writing related skills such as fine motor skills, eye-hand coordination
          and concepts of orientations before children begin specific writing activities of Chinese characters in the 4
          Year Old Class. Teachers observe each child so they are aware of each child's individual readiness. For
          English, within the context of writing for real purposes, such as creating lists, signs or writing letters or
          messages, children learn about sounds, letters, words and sentences, and talk about what they have written.
          Teachers carefully observe the indicators of writing readiness and encourage the children into writing or
          pre-writing experiences according to their skills and interests.
        </p>
        <h5>Reading in English</h5>
        <p>
          We aim for them to make meaning of what they are reading as they are ready. We don't want children to only be
          recognising words by memorisation. This is why we do not send flashcards home of letters or words. Our goal is
          to develop reading skills by beginning to understand the connection between letters and their sounds, and
          later on with words. Activities such as these are much more beneficial for a child's English reading
          development.
        </p>
        <p>
          Children in all classes are exposed to phonics at Kingston. Once the children reach the 4 Year Old Class,
          there is a more concentrated focus on exposing the children to the connection between the letters and their
          sounds, and providing reading strategies when looking at text. Some children will be ready to implement these
          learning strategies in their reading and writing activities. Other children will be exposed to phonics but
          will not yet be ready to put it into practice. Remember, each child is an individual and children in the same
          class will be developing at different rates. A formal phonics program begins at KIS in Year One when children
          are ready to put into practice the understandings they have gained through experimental reading and writing in
          KIK.
        </p>
        <h5>Reading in Chinese</h5>
        <p>
          Our goal is for all children to develop interest in reading and form positive reading habits. Our aim before
          the children move to Primary School is for them to recognise common words (with flashcards) and those that are
          related to the various units of inquiry. Teachers use words and sentences that can be easily understood by
          children, and then gradually increase the use of characters as appropriate. We also encourage children to
          accumulate the use of these characters and expand their recognition of vocabulary.
        </p>
        <h5>Listening and Speaking</h5>
        <p>
          Teachers at Kingston model the correct use of language and students are encouraged to make eye contact when
          communicating. During their time at school students have many opportunities to listen and speak – during play,
          dramatic play, unit work, general discussions and at Circle Time. Teachers allow children sufficient time to
          think and speak and use songs, rhymes and stories to familiarise children with new vocabulary. Through our
          Units of Inquiry students learn to listen and speak with purpose and understanding in a wide range of
          situations.
        </p>
      </React.Fragment>
    </Collapisble>

    <Collapisble trigger="Mathematics">
      <React.Fragment>
        <p>
          Students at Kingston explore mathematical concepts during the four Units of Inquiry that are covered during
          the school year. Mathematics is taught in a way that allows the students to explore and learn through applying
          the concepts and skills to real world situations. There are many opportunities for hands on learning which
          encourages active participation and discussion between both teachers and students. During their time at
          Kingston students cover five strands of mathematics which are:
        </p>
        <ul className="list">
          <li>Number</li>
          <li>Data Handling</li>
          <li>Pattern and Function</li>
          <li>Measurement</li>
          <li>Shape and Space</li>
        </ul>
        <p>
          When students move from the 3 Year Old Classes to the 4 Year Old Classes they continue to work on and extend
          their understanding and application of these mathematical skills and concepts. Mathematics is taught in both
          Putonghua and English.
        </p>
      </React.Fragment>
    </Collapisble>

    <Collapisble trigger="Social Studies">
      <p>
        Social Studies in our 3 and 4 Year Old Classes is covered during the four Units of Inquiry covered during the
        year and the content is drawn from the five strands of our Social Studies scope and sequence. These strands
        focus on people, experiences, the environment, resources and how we interact with our environment.
      </p>
    </Collapisble>

    <Collapisble trigger="Science">
      <p>
        Kingston students learn about all three aspects (biological, chemical and physical) of science through the
        transdisciplinary themes. Students go on field trips, conduct experiments, work on hands on activities, make
        hypotheses conduct observations to further their knowledge, understanding and skills related to various
        scientific concepts related to the four strands of our Science scope and sequence.
      </p>
    </Collapisble>

    <Collapisble trigger="Personal, Social, and Physical Education (PSPE)">
      <p>
        To support the development of well rounded individuals students at Kingston also explore the subject areas of
        personal, social and physical education. We have a specialist teacher that teaches Physical Education to our 4
        Year Old Classes. Our 3 Year Old Classes also have weekly Physical Education classes taught by their class
        teachers. Physical Education at Kingston not only improves physical development, it also assists and improves
        social, emotional and mental development. Students explore the capabilities of their bodies through a range of
        challenging group and individual activities and games and learn how to cooperate with others and use equipment
        in a safe manner. Because of the transdisciplinary nature of the Primary Years Programme there is also close
        cooperation between the specialist teacher and the class teachers.
      </p>
    </Collapisble>

    <Collapisble trigger="Arts – Music, Art and Drama">
      <p>
        Through the integration of arts into our programme our students are able to freely express themselves visually
        or musically through a range of stimulating activities. Learning through the arts develops creativity,
        spontaneity, imagination, independence and confidence which are attributes that are valued in all facets of
        learning. Arts also allow our students to explore both their own and other cultures.
      </p>
    </Collapisble>
  </ContentLayout>
);
